/* eslint-disable no-restricted-properties */
import { useIntl } from '@weenat/wintl'
import { useOrgContext } from 'app/orgProvider'
import useToasts from 'app/src/hooks/useToasts'
import Card from 'app/src/kit/Card'
import DelimitedFlex from 'app/src/kit/DelimitedFlex'
import { TextFieldPrimitive } from 'app/src/kit/fields/TextField'
import Icons from 'app/src/kit/Icons'
import Modal, { ModalProps } from 'app/src/kit/Modal'
import QRCode from 'app/src/kit/QRCode'
import Text from 'app/src/kit/Text'
import { useLoginAs } from 'app/state'
import { useUserContext } from 'app/userProvider'
import { getVersion } from 'app/utils'
import { getClientVersion, getWintlVersion } from 'app/utils/getVersion'
import copy from 'copy-to-clipboard'
import { isNil } from 'lodash-es'

const DebugContextModal: FC<ModalProps> = ({ isOpen, close }) => {
  const { t } = useIntl()
  const [loginAs] = useLoginAs()
  const { me, profile } = useUserContext()
  const org = useOrgContext()

  const { addInfoToast } = useToasts()

  const debugUrl = new URL(
    `${window.location.protocol}//${window.location.hostname}${!isNil(window.location.port) ? `:${window.location.port}` : ''}${window.location.pathname}`
  )

  debugUrl.pathname = `/me/debug`
  debugUrl.searchParams.set('login_as', loginAs?.email ?? me.email)
  debugUrl.searchParams.set('redirect', window.location.pathname + window.location.search)

  const debugLink = debugUrl.toString()

  return (
    <Modal isOpen={isOpen} close={close} title={t('titles.debug')}>
      <Flex $gap='xl'>
        <Flex $flexDirection='column' $width={320} $gap='xl'>
          <Card>
            <Flex $flexDirection='column' $width={'100%'}>
              <Text $fontWeight='medium'>Share</Text>
              <Text $color={'grayscale.700'}>
                Share the link below so they can see the current page in the same context
              </Text>
            </Flex>

            <TextFieldPrimitive
              label='Debug Link'
              value={debugLink}
              rightAdornment={
                <Icons.Copy
                  onPress={() => {
                    copy(debugLink)
                    close?.()
                    addInfoToast('Bug report link copied!')
                  }}
                >
                  Copy Debug Link
                </Icons.Copy>
              }
              hideErrors
              readOnly
            />
          </Card>
          <Card $gap='lg'>
            <Flex $flexDirection='column' $width={'100%'}>
              <Text $fontWeight='medium'>Login on your phone</Text>
              <Text $color={'grayscale.700'}>
                Access the current page by scanning the QR code below
              </Text>
            </Flex>

            <Flex $flexDirection='column' $alignSelf='center' $mx={'auto'}>
              <QRCode value={debugLink} size={128} />
            </Flex>
          </Card>
        </Flex>
        <Card $p={'md'} $minWidth={480}>
          <DelimitedFlex $flexDirection='column' $p={'md'} $gap='sm'>
            <Text $fontWeight='medium'>User</Text>
            <Text>ID : {me.id}</Text>
            <Text>Email : {me.email}</Text>
            <Text>
              Name : {me.firstName} {me.lastName}
            </Text>
            <Text>Langage : {profile.language}</Text>
          </DelimitedFlex>

          <DelimitedFlex $flexDirection='column' $p={'md'} $gap='sm'>
            <Text $fontWeight='medium'>Farm</Text>
            <Text>ID : {org.id}</Text>
            <Text>Name : {org.name}</Text>
          </DelimitedFlex>

          {!isNil(org.network) ? (
            <DelimitedFlex $flexDirection='column' $p={'md'} $gap='sm'>
              <Text $fontWeight='medium'>Network</Text>
              <Text>ID : {org.network.id}</Text>
              <Text>Name : {org.network.name}</Text>
              <Text>Type : {org.networkType?.toString()}</Text>
            </DelimitedFlex>
          ) : null}

          <DelimitedFlex $flexDirection='column' $p={'md'} $gap='sm'>
            <Text $fontWeight='medium'>OS / Browser</Text>
            <Text>Platform : {navigator.userAgentData?.platform ?? navigator.platform}</Text>
            <Text>
              Browser :{' '}
              {navigator.userAgentData?.brands
                .filter((b) => !b.brand.includes('Not'))
                .map((b) => `${b.brand} ${b.version}`)
                .join(', ') ?? navigator.userAgent}
            </Text>
            <Text>Language : {navigator.language}</Text>
            <Text>Timezone : {Intl.DateTimeFormat().resolvedOptions().timeZone}</Text>
          </DelimitedFlex>

          <Flex $flexDirection='column' $p={'md'} $gap='sm'>
            <Text $fontWeight='medium'>App</Text>
            <Text>version {getVersion()}</Text>
            <Text>wintl {getWintlVersion()}</Text>
            <Text>client {getClientVersion()}</Text>
          </Flex>
        </Card>
      </Flex>
    </Modal>
  )
}

export default DebugContextModal
